// src/Services/UserMeetServices.js
import axiosInstance from "../API/axiosInstance";

// ==================== HELPERS ====================

/**
 * Extracts a user-friendly error message from an axios error.
 * Never exposes raw status codes to the user.
 */
const getErrorMessage = (err, fallback = "Something went wrong. Please try again.") => {
    // Server sent a message we can use
    const serverMsg = err?.response?.data?.message;
    if (serverMsg) return serverMsg;

    // Network / connection issue
    if (!err?.response) {
        return "Network error. Please check your connection and try again.";
    }

    // Map status codes to friendly messages (never shown raw to user)
    const status = err?.response?.status;
    switch (status) {
        case 400:
            return err?.response?.data?.message || "Invalid request. Please check your input.";
        case 401:
            return "Your session has expired. Please sign in again.";
        case 403:
            return "You don't have permission to perform this action.";
        case 404:
            return err?.response?.data?.message || "The requested resource was not found.";
        case 409:
            return err?.response?.data?.message || "A conflict occurred. Please try again.";
        case 429:
            return "Too many requests. Please slow down and try again.";
        case 500:
        case 502:
        case 503:
            return "Server error. Please try again in a moment.";
        default:
            return fallback;
    }
};

/**
 * Returns true if the error means the breakout no longer exists.
 * Callers should treat this as "already ended" and clean up locally.
 */
export const isBreakoutGoneError = (err) => {
    if (!err) return false;

    const status = err?.response?.status || err?.status || null;
    if (status === 404) return true;

    const serverMsg = String(
        err?.response?.data?.message || err?.message || ""
    ).toLowerCase();

    return (
        serverMsg.includes("active breakout not found") ||
        serverMsg.includes("breakout not found") ||
        serverMsg.includes("not found")
    );
};

/**
 * Returns true if the error means the meeting no longer exists.
 */
export const isMeetingGoneError = (err) => {
    if (!err) return false;

    const status = err?.response?.status || err?.status || null;
    if (status === 404) return true;

    const serverMsg = String(
        err?.response?.data?.message || err?.message || ""
    ).toLowerCase();

    return (
        serverMsg.includes("meeting not found") ||
        serverMsg.includes("meeting has ended") ||
        serverMsg.includes("not found")
    );
};

// ==================== MEETING MANAGEMENT ====================

export const createMeeting = async (meetingData) => {
    try {
        const { data } = await axiosInstance.post("/meet/create", meetingData, {
            useUserMeetApi: true,
        });
        return data;
    } catch (err) {
        throw new Error(getErrorMessage(err, "Failed to create meeting."));
    }
};

export const getAllMeetings = async () => {
    try {
        const { data } = await axiosInstance.get("/meet/all", {
            useUserMeetApi: true,
        });
        return data;
    } catch (err) {
        throw new Error(getErrorMessage(err, "Failed to load meetings."));
    }
};

export const getMeetingDetails = async (joinCode) => {
    try {
        const { data } = await axiosInstance.get(`/meet/${joinCode}`, {
            useUserMeetApi: true,
        });
        return data;
    } catch (err) {
        throw new Error(getErrorMessage(err, "Failed to load meeting details."));
    }
};

export const joinMeeting = async (joinCode) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/gate/${joinCode}`,
            {},
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        const status = err?.response?.status;

        // Special cases for joining
        if (status === 409) {
            const code = err?.response?.data?.code;
            if (code === "MEETING_FULL") {
                throw new Error("This meeting is full. Please try again later.");
            }
            if (code === "ALREADY_JOINED") {
                // Return the existing session data — not really an error
                return err?.response?.data;
            }
        }

        throw new Error(getErrorMessage(err, "Failed to join meeting."));
    }
};

export const leaveMeeting = async (meetingId) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/leave`,
            {},
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        // If meeting is already gone — treat as success silently
        if (isMeetingGoneError(err)) {
            console.log("ℹ️ Meeting already ended — leave treated as success");
            return { success: true, message: "Already left" };
        }
        throw new Error(getErrorMessage(err, "Failed to leave meeting."));
    }
};

export const endMeeting = async (meetingId) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/end`,
            {},
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        throw new Error(getErrorMessage(err, "Failed to end meeting."));
    }
};

export const getMyMeetings = async () => {
    try {
        const { data } = await axiosInstance.get("/meet/my", {
            useUserMeetApi: true,
        });
        return data;
    } catch (err) {
        throw new Error(getErrorMessage(err, "Failed to load your meetings."));
    }
};

// ==================== BREAKOUT ROOMS ====================

export const createBreakout = async (meetingId, breakoutData) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/breakout/create`,
            breakoutData,
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        const status = err?.response?.status;

        if (status === 409) {
            const code = err?.response?.data?.code;
            if (code === "USER_IN_BREAKOUT") {
                throw new Error(
                    "You are already in an active breakout room. Please leave it first."
                );
            }
        }

        if (status === 403) {
            throw new Error(
                "You must be an active participant in the meeting to create a breakout room."
            );
        }

        throw new Error(getErrorMessage(err, "Failed to create breakout room."));
    }
};

export const inviteToBreakout = async (meetingId, breakoutId, inviteeIds) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/breakout/${breakoutId}/invite`,
            { inviteeIds },
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        const status = err?.response?.status;

        if (status === 403) {
            throw new Error("Only the breakout owner can send invitations.");
        }

        if (status === 400) {
            throw new Error(
                "Some participants are not active in the meeting and could not be invited."
            );
        }

        throw new Error(getErrorMessage(err, "Failed to send invitations."));
    }
};

export const respondToBreakoutInvite = async (
    meetingId,
    breakoutId,
    accepted
) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/breakout/${breakoutId}/respond`,
            { accepted },
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        if (isBreakoutGoneError(err)) {
            throw new Error(
                "This breakout room is no longer available. It may have ended."
            );
        }

        throw new Error(
            getErrorMessage(
                err,
                accepted
                    ? "Failed to join breakout room."
                    : "Failed to decline the invitation."
            )
        );
    }
};

export const rejoinBreakout = async (meetingId, breakoutId) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/breakout/${breakoutId}/join`,
            {},
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        if (isBreakoutGoneError(err)) {
            throw new Error(
                "This breakout room has ended and is no longer available."
            );
        }

        throw new Error(getErrorMessage(err, "Failed to rejoin breakout room."));
    }
};

export const leaveBreakout = async (meetingId, breakoutId) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/breakout/${breakoutId}/leave`,
            {},
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        // ✅ 404 = breakout already ended — silently treat as success
        if (isBreakoutGoneError(err)) {
            console.log("ℹ️ Breakout already ended — leave treated as success");
            return { success: true, message: "Breakout already ended" };
        }

        throw new Error(getErrorMessage(err, "Failed to leave breakout room."));
    }
};

export const endBreakout = async (meetingId, breakoutId) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/breakout/${breakoutId}/end`,
            {},
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        // ✅ 404 = breakout already ended — silently treat as success
        if (isBreakoutGoneError(err)) {
            console.log("ℹ️ Breakout already ended on server — cleaning up locally");
            return { success: true, message: "Breakout already ended" };
        }

        if (err?.response?.status === 403) {
            throw new Error("Only the breakout room owner can end the room.");
        }

        throw new Error(getErrorMessage(err, "Failed to end breakout room."));
    }
};

// ==================== WAITING ROOM ====================

export const admitParticipant = async (meetingId, participantId) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/admit/${participantId}`,
            {},
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
            throw new Error("Only the meeting host can admit participants.");
        }
        if (status === 404) {
            throw new Error("This participant is no longer waiting to join.");
        }
        throw new Error(getErrorMessage(err, "Failed to admit participant."));
    }
};

export const denyParticipant = async (meetingId, participantId) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/deny/${participantId}`,
            {},
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
            throw new Error("Only the meeting host can deny participants.");
        }
        if (status === 404) {
            throw new Error("This participant is no longer in the waiting room.");
        }
        throw new Error(getErrorMessage(err, "Failed to deny participant."));
    }
};

// ==================== MEETING INVITE EMAIL ====================

export const sendMeetingInvite = async (meetingId, emails) => {
    try {
        const { data } = await axiosInstance.post(
            `/meet/${meetingId}/invite`,
            { emails },
            { useUserMeetApi: true }
        );
        return data;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
            throw new Error("Only the meeting creator can send invites.");
        }
        if (status === 400) {
            throw new Error("Please provide at least one valid email address.");
        }
        throw new Error(getErrorMessage(err, "Failed to send meeting invites."));
    }
};